// src/ui/uiController.js
// v6 - Refined input management, Removed initial hideAllOverlays call

// Note: This file now relies on sceneManager being available (imported or global)
// and authService/uiUtils providing showGeneralMessage globally or via window if needed as fallback.
import { signOutUser, claimAnonymousScores } from '../services/authService.js'; // Keep if needed for specific UI actions NOT triggered by auth state change
import { hasSpecialAccess, getSpecialAccessHoursRemaining, getSpecialAccessDisplayName } from '../services/specialAccessService.js';
import sceneManager from '../utils/SceneManager.js'; // Direct import is generally better

const UIController = {
    recentOverlayActions: {
        lastClosedOverlay: null,
        closeTime: 0
    },
    activeScene: null, // Reference to the current Phaser scene instance
    _isEnablingInput: false, // Flag to prevent race conditions when enabling input

    /**
     * Records the last overlay that was closed and the time.
     * Helps prevent rapid toggling issues.
     */
    recordOverlayClosure: function(overlayId) {
        this.recentOverlayActions.lastClosedOverlay = overlayId;
        this.recentOverlayActions.closeTime = Date.now();
    },

    /**
     * Initializes the UI Controller, setting up listeners for overlays and popups.
     * Should be called once the DOM is ready and potentially after Phaser boots.
     */
    init: function() {
        console.log("UI Controller initializing...");
        // Overlays should start hidden via CSS/HTML. No need to call hideAllOverlays here.

        // Setup interactions for specific popups/overlays
        this.setupPopupInteraction('ai-name-popup', 'ai-name-close-btn', 'ai-name-form', this.handleAiNameSubmit.bind(this));
        this.setupPopupInteraction('custom-name-popup', 'custom-name-close-btn', 'custom-name-form', this.handleCustomNameSubmit.bind(this));

        this.setupCloseButtonListener('auth-overlay', 'auth-close-btn');
        this.setupCloseButtonListener('leaderboard-overlay', 'leaderboard-close-btn');
        // Assuming gameover-mainmenu-btn acts as a close/navigate button
        this.setupCloseButtonListener('gameover-overlay', 'gameover-mainmenu-btn');
        this.setupCloseButtonListener('analytics-overlay', 'analytics-close-btn');
        this.setupCloseButtonListener('privacy-overlay', 'privacy-close-btn');
        this.setupCloseButtonListener('access-code-overlay', 'access-code-close-btn');

        this.setupAccessCodeEvents(); // Sets up the access code form submission

        console.log("UI Controller initialized successfully.");
    },

    /**
     * Sets up the event listeners for the Special Access Code overlay form and close button.
     */
     setupAccessCodeEvents: function() {
        const overlayId = 'access-code-overlay';
        const form = document.getElementById('access-code-form');
        // Close button listener is handled by setupCloseButtonListener now
        // const closeBtn = document.getElementById('access-code-close-btn');

        if (!form) { console.warn("Access code overlay form element not found, skipping setup"); return; }

        // --- Replace listener on form element ---
        const newForm = form.cloneNode(true); // Clone to remove old listeners
        form.parentNode.replaceChild(newForm, form); // Replace in DOM
        // --- End Replace ---

        newForm.addEventListener('submit', async (event) => {
          event.preventDefault(); // Prevent default form submission
          const codeInput = document.getElementById('special-access-code');
          const accessCode = codeInput?.value?.trim();

          if (!accessCode) {
              this.showOverlayMessage(overlayId, 'Please enter an access code', true);
              return;
          }

          const submitBtn = document.getElementById('access-code-submit-btn');
          if (submitBtn) { submitBtn.textContent = "Verifying..."; submitBtn.disabled = true; }

          try {
            this.showOverlayMessage(overlayId, '', false); // Clear previous message
            const { validateAccessCode } = await import('../services/specialAccessService.js'); // Import dynamically
            const result = await validateAccessCode(accessCode);

            this.showOverlayMessage(overlayId, result.message, !result.success); // Show result message

            if (result.success) {
              if (codeInput) codeInput.value = ''; // Clear input on success
              if (submitBtn) {
                   submitBtn.textContent = "Success!";
                   submitBtn.style.backgroundColor = "var(--color-success, #00CC00)"; // Use success color
              }

              // Short delay before hiding and triggering events/navigation
              setTimeout(() => {
                this.hideOverlay(overlayId); // Hide the overlay

                // Reset transition state (important before triggering auth change)
                if (window.fixTransitionState) window.fixTransitionState();

                // Dispatch event to notify the system of access change
                window.dispatchEvent(new CustomEvent('special-access-granted', {
                    detail: { sessionId: result.sessionId, expiryTime: result.expiryTime }
                }));

                // No automatic scene change here - let the 'special-access-granted' listener in index.js handle it

              }, 1000); // 1 second delay

            } else {
              // Reset button on failure
              if (submitBtn) { submitBtn.textContent = "Submit"; submitBtn.disabled = false; }
            }
          } catch (error) {
            console.error("Access code validation error:", error);
            this.showOverlayMessage(overlayId, "Error processing access code", true);
            if (submitBtn) { submitBtn.textContent = "Submit"; submitBtn.disabled = false; }
          }
        });
      },


    /**
     * Stores a reference to the currently active Phaser scene.
     * This is crucial for enabling/disabling Phaser's input system correctly.
     * @param {Phaser.Scene | null} scene - The active scene instance or null.
     */
    setActiveScene(scene) {
        if (scene && typeof scene.scene === 'object' && typeof scene.scene.key === 'string') {
            console.log(`%c[UIController] Setting active scene reference to ${scene.scene.key}`, 'color: #8888FF');
            this.activeScene = scene;
            // Safety check: Reset transition flag if setting a scene that thinks it's transitioning
            if (this.activeScene.isTransitioning) {
                console.warn(`[UIController] Active scene ${scene.scene.key} had stuck transition flag. Resetting.`);
                this.activeScene.isTransitioning = false;
            }
             // Enable input if no overlays are visible when setting the scene
            if (!this.isAnyOverlayVisible()) {
                 this.enableSceneInput();
            }
        } else if (scene === null) {
             console.log(`%c[UIController] Clearing active scene reference.`, 'color: #8888FF');
             this.activeScene = null;
        } else {
            console.warn(`[UIController] Attempted to set invalid active scene:`, scene);
        }
    },

    /**
     * Displays a specific overlay by ID, hiding others.
     * Manages scene input state.
     * @param {string} id - The HTML ID of the overlay element.
     */
    showOverlay: function(id) {
        const functionStartTime = Date.now();
        console.log(`%c[UIController ${functionStartTime}] Showing overlay: ${id}`, 'color: #8888FF');

        // --- Prevent rapid overlay toggling/conflicts ---
        const authOverlayElement = document.getElementById('auth-overlay');
        const isAuthOverlayVisible = authOverlayElement && (authOverlayElement.classList.contains('show') || authOverlayElement.style.display === 'flex');
        const authCloseTime = this.recentOverlayActions.lastClosedOverlay === 'auth-overlay' ? this.recentOverlayActions.closeTime : 0;
        const timeSinceAuthClose = Date.now() - authCloseTime;

        if (id !== 'auth-overlay' && (isAuthOverlayVisible || (this.recentOverlayActions.lastClosedOverlay === 'auth-overlay' && timeSinceAuthClose < 500))) {
            console.warn(`[UIController ${functionStartTime}] Preventing overlay "${id}" (Auth Overlay active/recently closed).`);
            return;
        }
        // --- End Prevention Logic ---

        try {
            // --- Disable Phaser input FIRST ---
            this.disableSceneInput();
            // --- End Disable Input ---

            let overlay = document.getElementById(id);
            if (!overlay || !document.body.contains(overlay)) {
                console.warn(`[UIController ${functionStartTime}] Overlay ${id} not found. Attempting recreate.`);
                const template = document.querySelector(`template#${id}-template`); // Assumes templates exist with ID pattern
                if (template) {
                    const clone = template.content.cloneNode(true);
                    document.body.appendChild(clone);
                    overlay = document.getElementById(id);
                    console.log(`[UIController ${functionStartTime}] Re-appended ${id}.`);
                    // Re-setup listeners if necessary (e.g., auth overlay)
                    if (id === 'auth-overlay' && typeof window.setupAuthEventListeners === 'function') {
                         window.setupAuthEventListeners(); // Re-run auth setup
                    }
                    // Add similar checks/setups for other dynamic overlays if needed
                } else {
                    console.error(`[UIController ${functionStartTime}] Cannot show overlay ${id}. Missing element and template.`);
                    // Use the general message utility if available, otherwise alert
                    if (typeof showGeneralMessage === 'function') {
                        showGeneralMessage(`UI Error: Could not display the ${id} panel.`, 3000, true);
                    } else {
                        alert(`UI Error: Could not display the ${id} panel.`);
                    }
                    this.enableSceneInput(); // Re-enable input on failure
                    return;
                }
            }

            if (overlay) {
                this.hideAllOverlays(id); // Hide others before showing the new one
                requestAnimationFrame(() => { // Ensure DOM updates before adding class
                    overlay.style.display = 'flex'; // Set display *before* adding class
                    overlay.style.opacity = '0'; // Start transparent
                    overlay.style.pointerEvents = 'auto'; // Enable interaction
                    void overlay.offsetHeight; // Force reflow
                    overlay.classList.add('show'); // Trigger CSS transition
                    console.log(`[UIController ${functionStartTime}] Added 'show' class to ${id}`);

                    // Attempt to focus the first focusable element
                    const focusable = overlay.querySelector('input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(.close-btn):not(:disabled), [tabindex]:not([tabindex="-1"])');
                    if (focusable) {
                        setTimeout(() => { // Delay focus slightly
                            try { focusable.focus({ preventScroll: true }); }
                            catch (e) { console.warn(`Focus failed for ${id}:`, e); }
                        }, 150);
                    }
                });
            } else {
                console.error(`[UIController ${functionStartTime}] Overlay ${id} still not found after check/re-append.`);
                this.enableSceneInput(); // Re-enable input on failure
            }

        } catch (e) {
            console.error(`[UIController ${functionStartTime}] Error in showOverlay(${id}):`, e);
            this.enableSceneInput(); // Attempt to re-enable input on error
        }
    },

    /**
     * Hides a specific overlay by ID with animation.
     * Enables scene input if no other overlays are visible after hiding.
     * @param {string} id - The HTML ID of the overlay element.
     */
    hideOverlay: function(id) {
        const functionStartTime = Date.now();
        console.log(`%c[UIController ${functionStartTime}] Hiding overlay: ${id}`, 'color: #8888FF');
        try {
            const overlay = document.getElementById(id);
            // Check if the overlay exists and is currently considered "shown"
            if (overlay && (overlay.classList.contains('show') || overlay.style.display === 'flex')) {
                overlay.classList.remove('show'); // Start CSS transition
                overlay.style.pointerEvents = 'none'; // Disable interaction immediately
                this.recordOverlayClosure(id); // Record closure time

                let transitionEnded = false;
                const transitionEndHandler = (event) => {
                    // Ensure the event is for opacity and on the correct element
                    if (event.propertyName !== 'opacity' || event.target !== overlay) return;

                    overlay.removeEventListener('transitionend', transitionEndHandler);
                    transitionEnded = true; // Mark transition as finished

                    // Check again if the 'show' class wasn't re-added during the transition
                    if (!overlay.classList.contains('show')) {
                        overlay.style.display = 'none'; // Hide completely
                        // console.log(`[UIController ${functionStartTime}] ${id} display set to none (transitionend).`);
                        // Check if ANY other overlays are still visible before enabling input
                        if (!this.isAnyOverlayVisible()) {
                            this.enableSceneInput();
                        }
                    }
                };
                overlay.addEventListener('transitionend', transitionEndHandler);

                // Fallback timeout: If transitionend doesn't fire within ~350ms, hide manually
                setTimeout(() => {
                    if (!transitionEnded && overlay) { // Check if transition hasn't fired and overlay exists
                        overlay.removeEventListener('transitionend', transitionEndHandler); // Clean up listener
                         if (!overlay.classList.contains('show')) { // Check class again
                             if (overlay.style.display !== 'none') {
                                 overlay.style.display = 'none';
                                 // console.log(`[UIController ${functionStartTime}] ${id} display set to none (fallback timeout).`);
                             }
                             // Check if ANY other overlays are still visible before enabling input
                             if (!this.isAnyOverlayVisible()) {
                                 this.enableSceneInput();
                             }
                         }
                    }
                }, 350); // Slightly longer than CSS transition duration

            } else {
                 // If overlay wasn't showing, still check if we need to enable input (e.g., hiding non-existent overlay)
                 if (!this.isAnyOverlayVisible()) {
                     this.enableSceneInput();
                 }
            }
        } catch (e) {
            console.error(`[UIController ${functionStartTime}] Error in hideOverlay(${id}):`, e);
            this.enableSceneInput(); // Attempt to enable input on error
        }
    },

    /**
     * Hides all overlays currently visible, except for an optional one to exclude.
     * @param {string | null} excludeId - The ID of an overlay to keep visible.
     */
    hideAllOverlays: function(excludeId = null) {
        // console.log(`%c[UIController] Hiding all overlays${excludeId ? ` except ${excludeId}`: ''}`, 'color: #8888FF');
        let anyOverlaysWereVisible = this.isAnyOverlayVisible(); // Check state BEFORE hiding
        try {
            document.querySelectorAll('.overlay').forEach(overlay => {
                if (overlay.id !== excludeId) {
                    // Call hideOverlay for each one to trigger animations and input checks correctly
                    this.hideOverlay(overlay.id);
                }
            });

            // Input enabling is handled by the individual hideOverlay calls when the *last* one finishes hiding.

        } catch (e) {
            console.error("[UIController] Error in hideAllOverlays:", e);
            this.enableSceneInput(); // Attempt to enable on error
        }
    },

    /**
     * Displays a specific popup overlay. Disables Phaser input.
     * @param {string} popupId - The HTML ID of the popup overlay element.
     */
    showPopup(popupId) {
        console.log(`%c[UIController] Showing popup: ${popupId}`, 'color: #8888FF');
        this.disableSceneInput(); // Disable Phaser input when popup appears
        this.showOverlay(popupId); // Use the standard showOverlay logic
    },

    /**
     * Hides a specific popup overlay. Attempts to re-enable Phaser input if no other overlays are visible.
     * @param {string} popupId - The HTML ID of the popup overlay element.
     */
    hidePopup(popupId) {
        console.log(`%c[UIController] Hiding popup: ${popupId}`, 'color: #8888FF');
        this.hideOverlay(popupId); // Use standard hideOverlay logic (which handles input enabling)
    },

    /**
     * Disables input for the currently active Phaser scene and the game container.
     */
    disableSceneInput() {
        if (sceneManager.isTransitioning) {
            // console.log(`[UIController] Input disable request ignored (SceneManager is transitioning).`);
            return;
        }
        const sceneKey = this.activeScene?.scene?.key || 'N/A';
        // Disable Phaser Scene Input
        if (this.activeScene?.input?.enabled) {
            try {
                this.activeScene.input.enabled = false;
                console.log(`%c[UIController] Phaser input DISABLED for scene: ${sceneKey}`, 'color: #FFAAAA');
            } catch (e) { console.error(`[UIController] Error disabling scene input for ${sceneKey}:`, e); }
        }
        // Disable Pointer Events on Game Container
        const gameContainer = document.getElementById('game-container');
        if (gameContainer && gameContainer.style.pointerEvents !== 'none') {
            gameContainer.style.pointerEvents = 'none';
            // console.log("[UIController] Game container pointer events DISABLED.");
        }
    },

    /**
     * Enables input for the currently active Phaser scene and the game container,
     * ONLY IF no overlays are visible and the scene manager is not transitioning.
     */
    enableSceneInput: function() {
        // Prevent redundant calls or calls while enabling is in progress
        if (this._isEnablingInput) return;
        this._isEnablingInput = true;
        const functionStartTime = Date.now();

        try {
            // --- Primary Checks: Overlays and Transitions ---
            const overlayVisible = this.isAnyOverlayVisible();
            if (overlayVisible) {
                // console.log(`[UIController ${functionStartTime}] Input enable SKIPPED (Overlay visible).`);
                const gameContainer = document.getElementById('game-container');
                if (gameContainer) gameContainer.style.pointerEvents = 'auto'; // FIXED: Allow touch events for mobile scrolling
                return; // Exit early
            }
            if (sceneManager && sceneManager.isTransitioning) {
                 // console.log(`[UIController ${functionStartTime}] Input enable SKIPPED (SceneManager is transitioning).`);
                 const gameContainer = document.getElementById('game-container');
                 if (gameContainer) gameContainer.style.pointerEvents = 'auto'; // FIXED: Allow touch events for mobile scrolling
                 return; // Exit early
            }
            // --- End Checks ---

            // Get the current scene instance robustly
            const currentActiveScene = this.activeScene || sceneManager?.getActiveSceneInstance();
            const sceneKey = currentActiveScene?.scene?.key || 'N/A';
            let phaserInputWasEnabled = false;

            if (currentActiveScene?.input) {
                if (!currentActiveScene.input.enabled) {
                    try {
                        currentActiveScene.input.enabled = true;
                        phaserInputWasEnabled = true;
                        console.log(`%c[UIController ${functionStartTime}] Phaser input ENABLED for scene: ${sceneKey}`, 'color: #AAFFAA');
                    } catch (e) {
                        console.error(`[UIController ${functionStartTime}] Error enabling scene input for ${sceneKey}:`, e);
                    }
                } else {
                    phaserInputWasEnabled = true; // Already enabled
                    // console.log(`[UIController ${functionStartTime}] Phaser input ALREADY ENABLED for scene: ${sceneKey}`);
                }
            } else {
                 console.warn(`[UIController ${functionStartTime}] No valid scene/input object found for scene key: ${sceneKey}. Cannot enable Phaser input.`);
            }

            // Attempt to focus canvas only if Phaser input is now enabled
            if (phaserInputWasEnabled) {
                this.focusCanvas();
            }

            // Enable pointer events on the game container
            const gameContainer = document.getElementById('game-container');
            if (gameContainer && gameContainer.style.pointerEvents !== 'auto') {
                gameContainer.style.pointerEvents = 'auto';
                console.log(`[UIController ${functionStartTime}] Game container pointer events ENABLED ('auto').`);
            }

        } finally {
            // Reset the flag IMPORTANTLY in a finally block
            this._isEnablingInput = false;
        }
    },

    /**
     * Attempts to focus the Phaser game canvas.
     */
    focusCanvas() {
        try {
            // Find the canvas within the game container
            const canvas = document.querySelector('#game-container canvas');
            // Check if canvas exists and is not already the active element
            if (canvas && document.activeElement !== canvas) {
                // Ensure it's focusable (needed in some browsers)
                if (!canvas.hasAttribute('tabindex')) canvas.setAttribute('tabindex', '-1');
                // Focus without scrolling the page
                canvas.focus({ preventScroll: true });
                // console.log("[UIController] Attempted canvas focus.");
            }
        } catch (e) { console.warn("[UIController] Error focusing canvas:", e); }
    },

    /**
     * Safely sets up a click listener for a close button on an overlay.
     * Replaces existing listeners to prevent duplicates.
     * @param {string} overlayId - The ID of the overlay containing the button.
     * @param {string} closeBtnId - The ID of the close button element.
     */
    setupCloseButtonListener(overlayId, closeBtnId) {
        const closeBtn = document.getElementById(closeBtnId);
        if (closeBtn) {
             // Clone and replace to ensure only one listener is attached
             const newBtn = closeBtn.cloneNode(true);
             closeBtn.parentNode.replaceChild(newBtn, closeBtn);
            // Add the event listener to the new button
            newBtn.addEventListener('click', (event) => {
                event.stopPropagation(); // Prevent clicks propagating to overlay background
                this.hideOverlay(overlayId); // Call the hide function
            });
        } else {
            // console.warn(`UIController: Close button '${closeBtnId}' not found for overlay '${overlayId}'.`);
        }
    },

    /**
     * Safely sets up interaction listeners for popup overlays (close button and form submit).
     * Replaces existing listeners to prevent duplicates.
     * @param {string} popupId - The ID of the popup overlay element.
     * @param {string} closeBtnId - The ID of the close button element.
     * @param {string} formId - The ID of the form element within the popup.
     * @param {Function} submitCallback - The function to call on form submission.
     */
    setupPopupInteraction(popupId, closeBtnId, formId, submitCallback) {
        const popup = document.getElementById(popupId);
        if (!popup) {
            // console.warn(`UIController: Popup element '#${popupId}' not found for interaction setup.`);
            return;
        }

        // Setup close button listener
        const closeBtn = document.getElementById(closeBtnId);
        if (closeBtn) {
            const newCloseBtn = closeBtn.cloneNode(true);
            closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
            newCloseBtn.addEventListener('click', (event) => {
                event.stopPropagation(); // Prevent background clicks
                this.hidePopup(popupId); // Use hidePopup for specific popup logic
            });
        } else {
            // console.warn(`UIController: Close button '#${closeBtnId}' not found for popup '#${popupId}'.`);
        }

        // Setup form submit listener
        const form = document.getElementById(formId);
        if (form && submitCallback) {
            // Clone and replace form to remove previous submit listeners
            const newForm = form.cloneNode(true);
            // Copy values from old form elements to new ones (important for forms)
            Array.from(form.elements).forEach((el, index) => {
                 if (newForm.elements[index]) {
                    // Handle different input types
                    if (el.type === 'checkbox' || el.type === 'radio') {
                       newForm.elements[index].checked = el.checked;
                    } else {
                       newForm.elements[index].value = el.value;
                    }
                    newForm.elements[index].disabled = el.disabled; // Preserve disabled state
                 }
             });
            form.parentNode.replaceChild(newForm, form);
            // Add submit listener to the new form
            newForm.addEventListener('submit', (event) => {
                event.preventDefault(); // Prevent default form submission
                try {
                    submitCallback(); // Call the provided callback
                } catch (e) {
                    console.error(`Error in popup submit callback for ${popupId}:`, e);
                    this.showOverlayMessage(popupId, `Error: ${e.message || 'Could not process request.'}`, true);
                }
            });
        } else if (!form && submitCallback) {
            // console.warn(`UIController: Form '#${formId}' not found for popup '#${popupId}'.`);
        }
    },

    /**
     * Checks if any overlay managed by UIController is currently considered visible.
     * @returns {boolean} True if at least one overlay is visible, false otherwise.
     */
    isAnyOverlayVisible: function() {
        // Select elements that are either styled with display:flex or have the .show class
        const anyOverlayVisible = document.querySelector('.overlay.show, .overlay[style*="display: flex"]');
        // Return true if such an element exists, false otherwise
        return !!anyOverlayVisible;
    },

    /**
     * Handles the submission of the AI Team Name popup form.
     */
    handleAiNameSubmit() {
        console.log("UIController: Handling AI Name Submit");
        const popupId = 'ai-name-popup';
        const keywords = [
            document.getElementById('ai-keyword1')?.value.trim() || '',
            document.getElementById('ai-keyword2')?.value.trim() || '',
            document.getElementById('ai-keyword3')?.value.trim() || ''
        ];

        // Validation
        if (keywords.some(k => k.length === 0)) {
            this.showOverlayMessage(popupId, "Please enter all 3 keywords.", true);
            return;
        }
        if (keywords.some(k => k.length > 12)) {
            this.showOverlayMessage(popupId, "Keywords max 12 characters.", true);
            return;
        }

        this.hidePopup(popupId); // Hide popup before calling scene method

        // Call the active scene's method (expected to be LobbyScene)
        if (this.activeScene && typeof this.activeScene.generateAiTeamName === 'function') {
            // Double-check if the active scene is indeed the LobbyScene for safety
            if (this.activeScene.scene.key === 'LobbyScene') {
                this.activeScene.generateAiTeamName(keywords); // Pass validated keywords
            } else {
                console.warn("AI Name Submit called, but active scene is not LobbyScene.");
                // Optionally show a general error message
                if (typeof showGeneralMessage === 'function') showGeneralMessage("Cannot generate name now.", 2000, true);
            }
        } else {
            console.error("UIController: Cannot call generateAiTeamName on active scene or method missing.");
            if (typeof showGeneralMessage === 'function') showGeneralMessage("Error processing AI Name request.", 2000, true);
            else alert("Error processing AI Name generation request.");
        }
    },

    /**
     * Handles the submission of the Custom Team Name popup form.
     */
    handleCustomNameSubmit() {
        console.log("UIController: Handling Custom Name Submit");
        const popupId = 'custom-name-popup';
        const inputField = document.getElementById('custom-team-name');
        const newName = inputField?.value.trim() || '';

        // Validation
        if (newName.length < 1 || newName.length > 20) {
            this.showOverlayMessage(popupId, "Team name must be 1-20 characters.", true);
            return;
        }

        this.hidePopup(popupId); // Hide popup before calling scene method

        // Call the active scene's method (expected to be LobbyScene)
        if (this.activeScene && typeof this.activeScene.updateTeamName === 'function') {
            try {
                this.activeScene.updateTeamName(newName);
            } catch (e) {
                console.error("Error in activeScene.updateTeamName:", e);
                if (typeof showGeneralMessage === 'function') showGeneralMessage("Error updating name.", 2000, true);
                else alert("Error updating name.");
            }
        } else {
            console.error("UIController: Cannot call updateTeamName on active scene or method missing.");
            if (typeof showGeneralMessage === 'function') showGeneralMessage("Error processing custom name request.", 2000, true);
            else alert("Error processing custom name request.");
        }
    },

    /**
     * Displays a message within a specific overlay's message area.
     * @param {string} overlayId - The ID of the target overlay.
     * @param {string} message - The text message to display.
     * @param {boolean} [isError=true] - True for error styling, false for success/info.
     * @param {number} [duration=3500] - Auto-hide duration in ms. 0 for persistent.
     */
    showOverlayMessage(overlayId, message, isError = true, duration = 3500) {
        const overlay = document.getElementById(overlayId);
        if (!overlay) {
            console.error(`Cannot show message: Overlay not found: #${overlayId}`);
            // Fallback to general message if overlay doesn't exist
            if (typeof showGeneralMessage === 'function') showGeneralMessage(message, duration, isError);
            else alert(`${isError ? 'Error' : 'Info'}: ${message}`);
            return;
        }

        // Find or create the message area within the overlay content
        let messageDiv = overlay.querySelector('.message-area');
        if (!messageDiv) {
             const contentArea = overlay.querySelector('.overlay-content');
             if (contentArea) {
                  messageDiv = document.createElement('div');
                  messageDiv.className = 'message-area'; // Apply base class
                  // Try to insert before the button group for better placement
                  const buttonGroup = contentArea.querySelector('.button-group');
                  if (buttonGroup) contentArea.insertBefore(messageDiv, buttonGroup);
                  else contentArea.appendChild(messageDiv); // Append if no button group
             } else {
                  // Cannot find content area, fallback to general message
                  console.error(`Cannot add message area: Overlay content not found for #${overlayId}`);
                   if (typeof showGeneralMessage === 'function') showGeneralMessage(message, duration, isError);
                  else alert(`${isError ? 'Error' : 'Info'}: ${message}`);
                  return;
             }
        }

        // Set message text and style
        messageDiv.textContent = message;
        // Apply appropriate class based on error status
        messageDiv.className = `message-area ${isError ? 'error-message' : 'success-message'}`;
        messageDiv.style.display = message ? 'block' : 'none'; // Show/hide based on message content
        messageDiv.style.opacity = '1'; // Ensure visible

        // Clear any existing hide timer
        if (messageDiv.timeoutId) {
            clearTimeout(messageDiv.timeoutId);
            messageDiv.timeoutId = null;
        }

        // Set new hide timer if duration is positive
        if (duration > 0 && message) {
             messageDiv.timeoutId = setTimeout(() => {
                messageDiv.style.opacity = '0'; // Start fade out
                // Use another timeout to set display:none after fade out
                setTimeout(() => {
                    if (messageDiv) { // Check if element still exists
                        messageDiv.style.display = 'none';
                        messageDiv.timeoutId = null; // Clear ID after use
                    }
                 }, 300); // Match CSS transition duration
             }, duration);
        }
    }
};

// --- Initialization ---
// Delay initialization slightly to ensure DOM is fully ready and other scripts might have loaded
if (typeof window !== 'undefined') {
    const initializeUIController = () => {
        if (!window.uiControllerInitialized) {
            UIController.init();
            window.uiControllerInitialized = true;
            window.UIController = UIController; // Expose globally AFTER init
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeUIController);
    } else {
        // DOM already loaded, or script loaded async
        setTimeout(initializeUIController, 0); // Use setTimeout to ensure it runs after current script execution block
    }
}
// --- End Initialization ---

export default UIController;